/**
 * BYO Cart Sync
 *
 * When a BYO parent item's quantity changes in the cart, this helper
 * updates the parent AND proportionally scales all child items that
 * share the same _byo_id in a single /cart/update.js call.
 *
 * Usage:
 *   const result = await window.byoCartSync(itemKey, newQuantity)
 *   result.handled  — true if this was a BYO parent (caller should NOT
 *                      make its own /cart/change.js call)
 *   result.cart      — updated cart JSON (when handled is true)
 *
 * If the item is NOT a BYO parent, returns { handled: false } and the
 * caller should proceed with its normal cart change logic.
 */

(function () {
  async function byoCartSync(lineKey, newQty) {
    /* Grab the current cart so we can inspect properties */
    var cartRes = await fetch('/cart.js', { credentials: 'include' });
    var cart = await cartRes.json();

    /* Find the item by line key first (precise), fall back to variant ID */
    var variantId = String(lineKey).split(':')[0];
    var item = cart.items.find(function (ci) { return ci.key === lineKey; });
    if (!item) {
      /* Key may have changed (Cart Transform) — try variant ID but only match BYO parents */
      item = cart.items.find(function (ci) {
        if (String(ci.id) !== String(variantId)) return false;
        var p = ci.properties || {};
        return p._byo_id && !p._parent_variant_id;
      });
    }
    if (!item) return { handled: false };

    /* Only act if this item is a BYO *parent* (has _byo_id but NOT _parent_variant_id) */
    var props = item.properties || {};
    var byoId = props._byo_id;
    if (!byoId || props._parent_variant_id) return { handled: false };

    var oldQty = item.quantity;
    if (oldQty === 0) return { handled: false };

    /* Build an updates map keyed by line-item key.
       Include the parent AND proportionally scale all children. */
    var updates = {};

    /* Parent */
    updates[item.key] = newQty;

    /* Children */
    cart.items.forEach(function (ci) {
      if (ci === item) return;
      var cp = ci.properties || {};
      if (cp._byo_id === byoId && cp._parent_variant_id) {
        if (newQty === 0) {
          updates[ci.key] = 0;
        } else {
          var perPack = ci.quantity / oldQty;
          updates[ci.key] = Math.round(perPack * newQty);
        }
      }
    });

    var updateRes = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ updates: updates }),
    });

    var updatedCart = await updateRes.json();
    return { handled: true, cart: updatedCart };
  }

  window.byoCartSync = byoCartSync;
})();
